import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SelectWorkflowComponent } from './common/selectcomponent'

const methods: Record<string, Map<string, string>> = {}
methods[Provider.BigQuery] = new Map([
  ['max', 'MAX($1) $1_max'],
  ['min', 'MIN($1) $1_min'],
  ['sum', 'SUM($1) $1_sum'],
  ['count', 'COUNT($1) $1_count'],
  ['count_distinct', 'COUNT(DISTINCT $1) $1_count_distinct'],
  ['concat', 'STRING_AGG($1) $1_concat'],
  ['avg', 'AVG($1) $1_avg'],
  ['combine', 'ST_UNION_AGG($1) $1_union'],
  ['any', 'ANY_VALUE($1) $1_any'],
  ['array', 'ARRAY_AGG($1) $1_array'],
  ['centroid', 'ST_CENTROID_AGG($1) $1_centroid'],
  ['makeline', 'ST_MAKELINE(ARRAY_AGG($1)) $1_makeline'],
  ['convexhull', 'ST_CONVEXHULL(ST_UNION_AGG($1)) $1_convexhull']
])
methods[Provider.Snowflake] = new Map([
  ['max', 'MAX($1) $1_max'],
  ['min', 'MIN($1) $1_min'],
  ['mode', 'MODE($1) $1_mode'],
  ['sum', 'SUM($1) $1_sum'],
  ['count', 'COUNT($1) $1_count'],
  ['count_distinct', 'COUNT(DISTINCT $1) $1_count_distinct'],
  ['concat', "LISTAGG($1, ',') $1_concat"],
  ['avg', 'AVG($1) $1_avg'],
  ['combine', 'ST_COLLECT($1) $1_union'],
  ['any', 'ANY_VALUE($1) $1_any'],
  ['array', 'ARRAY_AGG($1) $1_array'],
  ['centroid', 'ST_CENTROID($1) $1_centroid']
])
methods[Provider.Postgres] = new Map([
  ['max', 'MAX($1) $1_max'],
  ['min', 'MIN($1) $1_min'],
  ['mode', 'MODE() WITHIN GROUP (ORDER BY $1) $1_mode'],
  ['sum', 'SUM($1) $1_sum'],
  ['count', 'COUNT($1) $1_count'],
  ['count_distinct', 'COUNT(DISTINCT $1) $1_count_distinct'],
  ['concat', "STRING_AGG($1, ',') $1_concat"],
  ['avg', 'AVG($1) $1_avg'],
  ['combine', 'ST_COLLECT($1) $1_union'],
  ['any', 'COALESCE($1) $1_any'],
  ['array', 'ARRAY_AGG($1) $1_array'],
  ['centroid', 'ST_CENTROID(ST_COLLECT($1)) $1_centroid'],
  ['makeline', 'ST_MAKELINE(ARRAY_AGG($1)) $1_makeline'],
  ['convexhull', 'ST_CONVEXHULL(ST_COLLECT($1)) $1_convexhull']
])
methods[Provider.Redshift] = new Map([
  ['max', 'MAX($1) $1_max'],
  ['min', 'MIN($1) $1_min'],
  ['mode', 'MODE() WITHIN GROUP (ORDER BY $1) $1_mode'],
  ['sum', 'SUM($1) $1_sum'],
  ['count', 'COUNT($1) $1_count'],
  ['count_distinct', 'COUNT(DISTINCT $1) $1_count_distinct'],
  ['concat', "LISTAGG(CAST($1 AS TEXT), ',') $1_concat"],
  ['avg', 'AVG($1) $1_avg'],
  ['combine', 'ST_COLLECT($1) $1_union'],
  ['any', 'ANY_VALUE($1) $1_any'],
  ['array', `JSON_PARSE('"[' || listagg($1, ',') || ']"') $1_array`],
  ['centroid', 'ST_CENTROID(ST_COLLECT($1)) $1_centroid'],
  ['convexhull', 'ST_CONVEXHULL(ST_COLLECT($1)) $1_convexhull']
])

methods[Provider.DatabricksRest] = new Map([
  ['max', 'MAX($1) $1_max'],
  ['min', 'MIN($1) $1_min'],
  ['mode', 'MODE($1) $1_mode'],
  ['sum', 'SUM($1) $1_sum'],
  ['count', 'COUNT($1) $1_count'],
  ['count_distinct', 'COUNT(DISTINCT $1) $1_count_distinct'],
  ['concat', `CONCAT_WS(',', COLLECT_LIST($1)) $1_concat'`],
  ['avg', 'AVG($1) $1_avg'],
  ['combine', 'ST_UNION_AGG($1) $1_union'],
  ['any', 'ANY_VALUE($1) $1_any'],
  ['array', 'ARRAY_AGG($1) $1_array'],
  ['centroid', 'ST_ASWKB(ST_CENTROID(ST_UNION_AGG(ST_GEOMFROMWKB($1)))) $1_centroid'],
  ['makeline', 'ST_ASWKB(ST_MAKELINE(ARRAY_AGG(ST_GEOMFROMWKB($1)))) $1_makeline'],
  ['convexhull', 'ST_ASWKB(ST_CONVEXHULL(ST_UNION_AGG(ST_GEOMFROMWKB($1)))) $1_convexhull']
])
const methodDataTypes: Record<string, Map<string, string>> = {}
methodDataTypes[Provider.BigQuery] = new Map([
  ['max', ColumnDataType.Number],
  ['min', ColumnDataType.Number],
  ['sum', ColumnDataType.Number],
  ['mode', ColumnDataType.Number],
  ['count', ColumnDataType.Number],
  ['count_distinct', ColumnDataType.Number],
  ['concat', ColumnDataType.String],
  ['avg', ColumnDataType.Number],
  ['combine', ColumnDataType.Geography],
  ['any', ColumnDataType.String],
  ['array', ColumnDataType.String],
  ['centroid', ColumnDataType.Geography],
  ['makeline', ColumnDataType.Geography],
  ['convexhull', ColumnDataType.Geometry]
])
methodDataTypes[Provider.Snowflake] = new Map([
  ['max', ColumnDataType.Number],
  ['min', ColumnDataType.Number],
  ['mode', ColumnDataType.Number],
  ['sum', ColumnDataType.Number],
  ['count', ColumnDataType.Number],
  ['count_distinct', ColumnDataType.Number],
  ['concat', ColumnDataType.String],
  ['avg', ColumnDataType.Number],
  ['combine', ColumnDataType.Geography],
  ['any', ColumnDataType.String],
  ['array', ColumnDataType.String]
])
methodDataTypes[Provider.Postgres] = new Map([
  ['max', ColumnDataType.Number],
  ['min', ColumnDataType.Number],
  ['mode', ColumnDataType.Number],
  ['sum', ColumnDataType.Number],
  ['count', ColumnDataType.Number],
  ['count_distinct', ColumnDataType.Number],
  ['concat', ColumnDataType.String],
  ['avg', ColumnDataType.Number],
  ['combine', ColumnDataType.Geometry],
  ['any', ColumnDataType.String],
  ['array', ColumnDataType.String],
  ['centroid', ColumnDataType.Geometry],
  ['makeline', ColumnDataType.Geometry],
  ['convexhull', ColumnDataType.Geometry]
])
methodDataTypes[Provider.Redshift] = new Map([
  ['max', ColumnDataType.Number],
  ['min', ColumnDataType.Number],
  ['sum', ColumnDataType.Number],
  ['count', ColumnDataType.Number],
  ['count_distinct', ColumnDataType.Number],
  ['concat', ColumnDataType.String],
  ['avg', ColumnDataType.Number],
  ['combine', ColumnDataType.Geography],
  ['any', ColumnDataType.String],
  ['array', ColumnDataType.String],
  ['centroid', ColumnDataType.Geography],
  ['convexhull', ColumnDataType.Geometry]
])
methodDataTypes[Provider.DatabricksRest] = new Map([
  ['max', ColumnDataType.Number],
  ['min', ColumnDataType.Number],
  ['sum', ColumnDataType.Number],
  ['count', ColumnDataType.Number],
  ['count_distinct', ColumnDataType.Number],
  ['concat', ColumnDataType.String],
  ['avg', ColumnDataType.Number],
  ['combine', ColumnDataType.Geography],
  ['any', ColumnDataType.String],
  ['array', ColumnDataType.String],
  ['centroid', ColumnDataType.Geography],
  ['makeline', ColumnDataType.Geography],
  ['convexhull', ColumnDataType.Geometry]
])

class Summarize extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.summarize',
        group: 'Aggregation',
        title: 'Summarize',
        description: `
      This component creates a new table with aggregated values for the input whole table.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'columns',
          type: WorkflowComponentParamType.SelectColumnAggregation,
          title: 'Aggregation',
          parent: 'source',
          placeholder: 'workflows.parameterForm.selectAField',
          allowExpression: false
        }
      ]
    )
  }

  getInnerSql({ envVars, params, connection }: WorkflowComponentCodeParams) {
    const tokens: Array<string> = ((params.columns as string) ?? '').split(',').map((x) => x.trim())
    const stats: Array<string> = []
    for (let index = 0; index < tokens.length; index += 2) {
      stats.push(
        (methods[connection.type].get(tokens[index + 1]) as string)
          .replace(/\$1/g, tokens[index])
          .replace(/\$ATLOCATION/g, connection.quoteName(envVars.analyticsToolboxDataset as string))
      )
    }
    if (connection.type === Provider.DatabricksRest) {
      return dedent`SELECT /*+ REPARTITION(40) */
                      ${stats.join(',\n')}
                  FROM ${connection.quoteName(params.source as string)}`
    } else {
      return dedent`SELECT
                      ${stats.join(',\n')}
                  FROM ${connection.quoteName(params.source as string)}`
    }
  }

  getUIProperties(provider?: Provider): Record<string, unknown> {
    const classifiedMethods: Record<string, Array<string>> = {}
    classifiedMethods[ColumnDataType.String] = ['concat', 'count', 'count_distinct', 'any', 'array']
    classifiedMethods[ColumnDataType.Number] = [
      'min',
      'max',
      'avg',
      'sum',
      'count',
      'count_distinct',
      'mode',
      'any',
      'array'
    ]
    classifiedMethods[ColumnDataType.Boolean] = ['count', 'count_distinct', 'any']
    classifiedMethods[ColumnDataType.Date] = ['min', 'max', 'count', 'count_distinct', 'any']
    classifiedMethods[ColumnDataType.Datetime] = ['min', 'max', 'count', 'count_distinct', 'any']
    classifiedMethods[ColumnDataType.Time] = ['min', 'max', 'count', 'count_distinct', 'any']
    classifiedMethods[ColumnDataType.Timestamp] = ['min', 'max', 'count', 'count_distinct', 'any']
    switch (provider as Provider) {
      case Provider.BigQuery:
        classifiedMethods[ColumnDataType.Geometry] = ['count', 'any', 'array']
        classifiedMethods[ColumnDataType.Geography] = [
          'combine',
          'count',
          'any',
          'array',
          'centroid',
          'makeline',
          'convexhull',
          'concavehull'
        ]
        break
      case Provider.Snowflake:
        classifiedMethods[ColumnDataType.Geometry] = ['count', 'any', 'array']
        classifiedMethods[ColumnDataType.Geography] = ['combine', 'count', 'any', 'array', 'concavehull']
        break
      case Provider.Redshift:
        classifiedMethods[ColumnDataType.Geometry] = ['combine', 'count', 'any', 'array', 'centroid', 'convexhull']
        classifiedMethods[ColumnDataType.Geography] = ['count', 'any', 'array']
        break
      case Provider.Postgres:
        classifiedMethods[ColumnDataType.Geometry] = [
          'combine',
          'count',
          'any',
          'array',
          'centroid',
          'makeline',
          'convexhull'
        ]
        classifiedMethods[ColumnDataType.Geography] = ['count', 'any', 'array']
        break
      case Provider.DatabricksRest:
        classifiedMethods[ColumnDataType.Geometry] = [
          'combine',
          'count',
          'any',
          'array',
          'centroid',
          'makeline',
          'convexhull'
        ]
        classifiedMethods[ColumnDataType.Geography] = ['count', 'any', 'array']
        break
    }
    return {
      methods: classifiedMethods
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ params, connection }: GetOutputSchemaParams): TableSchema {
    const tokens: Array<string> = ((params.columns as string) ?? '').split(',').map((x) => x.trim())
    const statColumns: Array<{ name: string; type: ColumnDataType }> = []
    for (let index = 0; index < tokens.length; index += 2) {
      const colName = methods[connection.type]
        .get(tokens[index + 1])
        ?.split(' ')
        .pop()
        ?.replace(/\$1/g, tokens[index]) as string
      statColumns.push({
        name: colName,
        type: methodDataTypes[connection.type].get(tokens[index + 1]) as ColumnDataType
      })
    }
    return statColumns
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { Summarize }
